import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import useClaimFlow from "../../../Hooks/u1/useClaimFlow";
import useClaimDispute from "../../../Hooks/u1/useClaimDispute";
import { Navbar } from "../../../components/common/u1/Navbar";
import { SignedInNavActions } from "../../../components/common/u1/SignedInNavActions";
import { cn } from "../../../components/common/u1/cn";
import { WIDE_MEASURE } from "../../../components/common/u1/measure";
import { Button } from "../../../components/common/u1/Button";
import { StepProgress } from "../../../components/common/u1/StepProgress";
import { ErrorNote } from "../../../components/common/u1/ErrorNote";
import { WhatHappensNext } from "../../../components/common/static/u1/WhatHappensNext";
import { NeedHelp } from "../../../components/common/static/u1/NeedHelp";
import {
  CompanyStep,
  LicenseStep,
  IndustryStep,
  AddressStep,
  ContactStep,
  SubmittedStep,
  DisputeStep,
  DisputeSubmittedStep,
} from "../../../components/public/ClaimBusiness/u1";
import { Countries } from "../../../components/public/Home/CountryFlag";
import { ArrowRight } from "../../../assets/ui/icons";
import "../../../components/common/u1/tokens.css";

/* ICC-11 — what happens after a dispute is raised. Deliberately promises a
   conversation and no outcome: support investigates, and the company does not
   move until they say so. */
const DISPUTE_STEPS = [
  {
    title: "Support reviews it",
    body: "Our team checks your details against the company's registration record.",
  },
  {
    title: "We may ask for more",
    body: "If anything is unclear we will email you at the address on this form.",
  },
  {
    title: "We put it right",
    body: "If the profile is yours, support transfers it and tells you it is done.",
  },
];

/* u1 claim intake — /claim/:id. */
export default function ClaimCompany() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state) => state.auth?.isLoggedIn);
  const f = useClaimFlow({ id });
  const d = useClaimDispute({ company: f.company });

  /* ICC-11 — a company that already has an owner cannot be claimed, so the
     page offers the one thing that is open to this user: disputing it. Two
     ways in, one screen. Either the record arrives already claimed (the
     profile's "Claim company" button, or this URL typed directly), or the
     claim is refused with ICC-02's `already_claimed` in a race. */
  const companyRecord = f.company?.data || f.company;
  const disputing = Boolean(companyRecord?.claimed) || f.blockedAsClaimed;

  /* ICC-01 leaves nothing in a queue to track, so a hand-typed business lands
     on My Businesses — the profile it now owns — rather than the requests list
     this used to wait in. */
  useEffect(() => {
    if (disputing || !f.isSubmitted || !f.searchable) return undefined;
    const timer = setTimeout(
      () => navigate("/dashboard/my-companies", { replace: true }),
      2500
    );
    return () => clearTimeout(timer);
  }, [disputing, f.isSubmitted, f.searchable, navigate]);

  /* The full country name, not the ISO code: "IN" tells the user nothing about
     which country their address is being filed under. */
  const countryName = Countries.find((c) => c.code === f.countryCode)?.name;
  const countryLabel = countryName
    ? `${countryName} (${f.countryCode})`
    : f.countryCode;

  const heading = disputing
    ? d.submitted
      ? "Dispute received"
      : "This company is already claimed"
    : f.isSubmitted
    ? "You're all set"
    : `Step ${f.step} of ${f.totalSteps} — ${f.steps[f.step - 1]}`;

  return (
    <div className="u1-root min-h-screen w-full bg-[var(--bg)] flex flex-col">
      {/* The header's right-hand side depends on who is looking — F-110. */}
      <Navbar
        actions={
          isLoggedIn ? (
            <SignedInNavActions />
          ) : (
            <Button
              variant="primary"
              onClick={() => navigate("/sign-up", { state: { redirectToStep: 6 } })}
              className="whitespace-nowrap"
            >
              List your business <ArrowRight size={14} />
            </Button>
          )
        }
      />

      {/* WIDE_MEASURE, the same bound the u1 Navbar above now uses — it replaced `max-w-7xl px-4 sm:px-6 lg:px-8`, which tracked the bar's old three-step padding. */}
      <main className={cn(WIDE_MEASURE, "flex-1 py-6")}>
        <button
          type="button"
          onClick={() => navigate(id ? `/search/${id}` : "/search")}
          className="mb-4 inline-flex items-center gap-1.5 rounded text-[13px] text-[var(--muted)] font-sans hover:text-[var(--ink)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--orange)]"
        >
          ← Back to search
        </button>

        {/* The rail drops below the form on narrow screens rather than being
            hidden — "what happens next" is the reassurance that keeps someone
            filling in five steps of licence detail. */}
        {/* grid-cols-1 is repeat(1, minmax(0,1fr)) — without it the implicit track is `auto` and a grid item's min-width:auto lets wide content (a long company name, a... */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-[minmax(0,1fr)_320px]">
          <section className="min-w-0 self-start rounded-2xl border border-[var(--line)] bg-white p-4 sm:p-6 md:p-8">
            <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--orange)] font-sans">
              {disputing ? "Dispute this claim" : "Access your business profile"}
            </span>

            <h1 className="mt-2 text-[22px] sm:text-[26px] font-bold tracking-[-0.02em] text-[var(--ink)] font-sans">
              {heading}
            </h1>

            {!f.isSubmitted && !disputing && (
              <>
                <p className="mt-2 max-w-[600px] text-[14px] leading-[1.55] text-[var(--ink-2)] font-sans">
                  {f.searchable && !f.search.name ? (
                    <>
                      Find your business in the TREVEX directory and claim it —
                      the profile is yours as soon as you finish.
                    </>
                  ) : f.companyName || f.search.name ? (
                    <>
                      Tell us about <b>{f.companyName || f.search.name}</b> and the
                      profile is yours as soon as you finish.
                    </>
                  ) : (
                    <>
                      Tell us about your business and the profile is yours as soon
                      as you finish.
                    </>
                  )}
                </p>

                <StepProgress steps={f.steps} current={f.step} className="mb-7" />
              </>
            )}

            {disputing ? (
              d.submitted ? (
                <DisputeSubmittedStep
                  companyName={d.companyName}
                  contactEmail={d.contactEmail}
                  ticketId={d.ticketId}
                  {...(id
                    ? { onBackToProfile: () => navigate(`/search/${id}`) }
                    : {})}
                  onHelp={() => navigate("/dashboard/help")}
                />
              ) : (
                <DisputeStep
                  companyName={d.companyName || f.companyName}
                  fullName={d.fullName}
                  onFullNameChange={d.setFullName}
                  contactEmail={d.contactEmail}
                  onContactEmailChange={d.setContactEmail}
                  designation={d.designation}
                  onDesignationChange={d.setDesignation}
                  phone={d.phone}
                  onPhoneChange={d.setPhone}
                  dialCountry={d.dialCountry}
                  onDialCountryChange={d.setDialCountry}
                  dialCodeOptions={d.dialCodeOptions}
                  reason={d.reason}
                  onReasonChange={d.setReason}
                  evidence={d.evidence}
                  onEvidenceChange={d.setEvidence}
                />
              )
            ) : f.companyLoading && f.step === 1 ? (
              <p role="status" className="py-10 text-center text-[14px] text-[var(--muted)] font-sans">
                Loading company details…
              </p>
            ) : (
              <>
                {f.step === 1 && (
                  <CompanyStep
                    country={countryLabel}
                    countryCode={f.searchable ? f.search.countryCode : f.countryCode}
                    companyName={f.searchable ? f.search.name : f.companyName}
                    nameLocked={!f.searchable && f.nameLocked}
                    onCompanyNameChange={
                      f.searchable ? f.search.changeName : f.setCompanyName
                    }
                    confirmed={f.confirmed}
                    onConfirmChange={f.setConfirmed}
                    errors={f.searchable ? f.search.errors : f.errors}
                    /* Search lives on this step when no company came from the
                       URL — the directory lookup and the "cannot find it"
                       fallback both belong here, not on a screen of their own. */
                    searchable={f.searchable}
                    onCountryChange={f.search.changeCountry}
                    onLocateCountry={f.search.locateCountry}
                    results={f.search.results}
                    isSearching={f.search.isLoading}
                    selectedKey={f.search.selectedKey}
                    onSelectCompany={f.search.selectCompany}
                    notFound={f.search.notFound}
                    onSwitchMode={f.search.switchMode}
                    minQuery={f.search.minQuery}
                  />
                )}

                {f.step === 2 && (
                  <LicenseStep
                    licenseNo={f.licenseNo}
                    onLicenseNoChange={f.setLicenseNo}
                    state={f.licenseState}
                    onStateChange={f.changeLicenseState}
                    stateOptions={f.stateOptions}
                    jurisdiction={f.jurisdiction}
                    onJurisdictionChange={f.changeJurisdiction}
                    jurisdictionOptions={f.jurisdictionOptions}
                    registrationBody={f.registrationBody}
                    onRegistrationBodyChange={f.setRegistrationBody}
                    registrationBodyOptions={f.registrationBodyOptions}
                    bodyAsText={!f.isUae}
                    file={f.licenseFile}
                    onFileChange={f.setLicenseFile}
                    errors={f.errors}
                  />
                )}

                {f.step === 3 && (
                  <IndustryStep
                    options={f.industryOptions}
                    value={f.industries}
                    onChange={f.setIndustries}
                    isLoading={f.industriesLoading}
                    errors={f.errors}
                  />
                )}

                {f.step === 4 && (
                  <AddressStep
                    line1={f.address.line1}
                    line2={f.address.line2}
                    area={f.address.area}
                    city={f.address.city}
                    postalCode={f.address.postalCode}
                    onFieldChange={f.setAddressField}
                    country={countryLabel}
                    countryCode={f.countryCode}
                    state={f.licenseState}
                    onStateChange={f.changeLicenseState}
                    stateOptions={f.stateOptions}
                    workingHours={f.workingHours}
                    onWorkingHoursChange={f.setWorkingHours}
                    errors={f.errors}
                  />
                )}

                {f.step === 5 && (
                  <ContactStep
                    email={f.contactEmail}
                    onEmailChange={f.setContactEmail}
                    dialCountry={f.dialCountry}
                    onDialCountryChange={f.setDialCountry}
                    dialCodeOptions={f.dialCodeOptions}
                    phone={f.phone}
                    onPhoneChange={f.setPhone}
                    errors={f.errors}
                  />
                )}

                {f.isSubmitted && (
                  <SubmittedStep
                    companyName={f.companyName || f.search.name}
                    {...(f.searchable
                      ? { note: "Taking you to your businesses…" }
                      : {
                          onMyBusinesses: () => navigate("/dashboard/my-companies"),
                          /* The claimed profile lives at the TREVEX id the
                             claim returned, which is NOT the route's id when
                             the company was reached by its WorldBox one —
                             claiming it is what mints the TREVEX company.
                             Sending the user to /search/<WIN> showed them the
                             old WorldBox record instead of what they just
                             claimed. */
                          onViewProfile: () =>
                            navigate(`/search/${f.claimedCompanyId || id}`),
                        })}
                  />
                )}
              </>
            )}

            {(disputing ? d.apiError : f.apiError) && (
              <ErrorNote>{disputing ? d.apiError : f.apiError}</ErrorNote>
            )}

            {disputing && !d.submitted && (
              <div className="mt-7 flex items-center gap-3 border-t border-[var(--line)] pt-5 sm:justify-between">
                <Button
                  variant="ghost"
                  onClick={() => navigate(id ? `/search/${id}` : "/search")}
                  className="h-[46px] basis-0 grow-[2] px-4 sm:grow-0 sm:basis-auto sm:px-5"
                >
                  ← Cancel
                </Button>
                <Button
                  onClick={d.submit}
                  disabled={!d.valid || d.isBusy}
                  className="h-[46px] basis-0 grow-[3] px-4 sm:grow-0 sm:basis-auto sm:px-5"
                >
                  {d.isBusy ? "Submitting…" : "Submit dispute"}
                  {!d.isBusy && <ArrowRight size={14} />}
                </Button>
              </div>
            )}

            {!disputing && !f.isSubmitted && (
              <div
                /* One row at every width. On phones the two share it 40/60 by flex-grow rather than fixed widths, so the split holds on a 320px screen and on a 430px one... */
                className="mt-7 flex items-center gap-3 border-t border-[var(--line)] pt-5 sm:justify-between"
              >
                <Button
                  variant="ghost"
                  onClick={f.back}
                  disabled={f.step === 1}
                  className="h-[46px] basis-0 grow-[2] px-4 disabled:opacity-40 sm:grow-0 sm:basis-auto sm:px-5"
                >
                  ← Back
                </Button>
                {/* ICC-03 — the licence step is the one step that can be
                    passed over entirely, so it is the only one offering this. */}
                {f.step === 2 && (
                  <Button
                    variant="ghost"
                    onClick={f.skipLicense}
                    disabled={f.isBusy}
                    className="h-[46px] px-4 text-[var(--ink-2)] sm:ml-auto sm:mr-1 sm:px-5"
                  >
                    Skip for now
                  </Button>
                )}
                <Button
                  onClick={f.next}
                  disabled={!f.stepValid || f.isBusy}
                  className="h-[46px] basis-0 grow-[3] px-4 sm:grow-0 sm:basis-auto sm:px-5"
                >
                  {f.isBusy
                    ? "Submitting…"
                    : f.step === f.totalSteps
                    ? "Claim this company"
                    : "Continue"}
                  {!f.isBusy && <ArrowRight size={14} />}
                </Button>
              </div>
            )}
          </section>

          <aside className="grid min-w-0 self-start content-start gap-4 md:sticky md:top-24">
            {/* The claim rail describes a profile changing hands, which is
                exactly what a dispute does not do. */}
            {disputing ? (
              <WhatHappensNext title="How a dispute works" steps={DISPUTE_STEPS} />
            ) : (
              <WhatHappensNext />
            )}
            <NeedHelp />
          </aside>
        </div>
      </main>
    </div>
  );
}
